/** @jsxImportSource @emotion/react */
import 'react-tooltip/dist/react-tooltip.css';

import { ClassNames, SerializedStyles } from '@emotion/react';
import * as React from 'react';
import { ITooltip, Tooltip as ReactTooltip, TooltipRefProps } from 'react-tooltip';

import colors from '../../../theme/colors';

interface Props extends ITooltip {
  innerRef?: React.Ref<TooltipRefProps>;
  theme?: 'dark' | 'light' | 'custom';
  primaryColor?: (typeof colors)[keyof typeof colors];
  secondaryColor?: (typeof colors)[keyof typeof colors];
  customCss?: SerializedStyles;
}

const isEmptyContent = (value: React.ReactNode): boolean =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

const Tooltip = ({
  children,
  theme = 'dark',
  primaryColor = colors.white,
  secondaryColor = colors.black,
  innerRef,
  customCss,
  style,
  opacity = 1,
  delayHide = 0,
  render,
  ...props
}: Props) => {
  const renderContent: NonNullable<ITooltip['render']> =
    render ??
    (({ content }) => {
      const resolved = content ?? children;
      return isEmptyContent(resolved) ? null : resolved;
    });

  return (
    <ClassNames>
      {({ css }) => (
        <ReactTooltip
          {...props}
          render={renderContent}
          className={
            theme === 'dark'
              ? css`
                  &.react-tooltip {
                    background-color: #726969;
                    border-radius: 2px;
                    box-shadow: 0 12px 12px rgba(0, 0, 0, 0.12);
                    color: #fff;
                    font-size: 14px;
                    font-weight: 500;
                    max-width: 400px;
                    white-space: pre-wrap;
                    z-index: 10000;
                  }

                  & > .react-tooltip-content-wrapper {
                    padding: 8px 6px;
                  }
                `
              : css`
                  &.react-tooltip {
                    border-radius: 0;
                    filter: drop-shadow(2px 4px 0px rgba(0, 0, 0, 0.12));
                    white-space: pre-wrap;
                    z-index: 10000;
                  }

                  & > .react-tooltip-content-wrapper {
                    padding: 8px 6px;
                  }

                  ${customCss};
                `
          }
          opacity={opacity}
          delayHide={delayHide}
          style={theme === 'dark' ? style : { backgroundColor: primaryColor, color: secondaryColor, ...style }}
          ref={innerRef}
        />
      )}
    </ClassNames>
  );
};

export default Tooltip;
